<script setup lang="ts">
import { onKeyDown } from '@vueuse/core'
import { inject } from 'vue'
import PlayerBox from '~/components/scoreapp/PlayerBox.vue'
import ThrowList from '~/components/scoreapp/ThrowList.vue'
import { GamePlayerSimple, getGameStore } from '~/stores/localGameStore'
import GameLegsOverview from '~/components/games/GameLegsOverview.vue'
import GameScreenShortCuts from '~/components/scoreapp/GameScreenShortCuts.vue'
import ScorePad from '~/components/scoreapp/ScorePad.vue'
import { UiButton } from '#components'

const hasError = ref(false)

const shouldDisableScorePad = ref(false)

const props = defineProps<{
  gameId: string
  customHeader: string | null
  canLeave: boolean
  scorePadSide?: 'left' | 'right'
  nextGameAction?: () => undefined | null
  noWriter?: boolean
  disabled?: boolean
  viewOnly?: boolean
  message?: { message: string, home: boolean } | null
  eventName?: string
  hideMenu?: boolean
}>()

const overlay = useOverlay()

const gameState = getGameStore(props.gameId)

const enableKeys = computed(() => !props.canLeave && gameState.hasBullWinner && overlay.overlays.length === 0)

if (!props.viewOnly) {
// Key mapping start.
  onKeyDown('1', () => {
    if (!enableKeys.value) {
      return
    }
    addToScore(1)
  })
  onKeyDown('2', () => {
    if (!enableKeys.value) {
      return
    }
    addToScore(2)
  })
  onKeyDown('3', () => {
    if (!enableKeys.value) {
      return
    }
    addToScore(3)
  })
  onKeyDown('4', () => {
    if (!enableKeys.value) {
      return
    }
    addToScore(4)
  })
  onKeyDown('5', () => {
    if (!enableKeys.value) {
      return
    }
    addToScore(5)
  })
  onKeyDown('6', () => {
    if (!enableKeys.value) {
      return
    }
    addToScore(6)
  })
  onKeyDown('7', () => {
    if (!enableKeys.value) {
      return
    }
    addToScore(7)
  })
  onKeyDown('8', () => {
    if (!enableKeys.value) {
      return
    }
    addToScore(8)
  })
  onKeyDown('9', () => {
    if (!enableKeys.value) {
      return
    }
    addToScore(9)
  })
  onKeyDown('0', () => {
    if (!enableKeys.value) {
      return
    }
    addToScore(0)
  })
  onKeyDown('Backspace', (e) => {
    if (!enableKeys.value) {
      return
    }
    e.preventDefault()
    cancelORRemoveFromScore()
  })
  onKeyDown('Enter', (e) => {
    if (!enableKeys.value) {
      return
    }
    e.preventDefault()
    submit()
  })
}
// Key mapping end.

watch(hasError, (newValue) => {
  if (newValue) {
    setTimeout(() => {
      hasError.value = false
    }, 300)
  }
})

const addToScore = (number: number) => {
  gameState.addToScore(number).catch(() => {
    hasError.value = true
  })
}

const onScoreTap = () => {
  if (gameState.scorePadIsGameShot) {
    submit()
  }
  else if (!gameState.getCorrectingScore) {
    gameState.submitScoreAsRemaining()
      .then(() => {
        scrollToBottom()
      })
      .catch(() => {
        hasError.value = true
      })
  }
  else {
    hasError.value = true
  }
}

const cancelORRemoveFromScore = () => {
  if (gameState.getCorrectingScore && gameState.scorePadValue === null) {
    gameState.cancelCorrectingScore()
  }
  else {
    gameState.removeFromScore()
  }
}

const setScoreAndSubmit = (number: number) => {
  gameState.addToScore(number).catch(() => {
    hasError.value = true
  })
  submit()
}

onMounted(() => {
  gameState.onSync = () => {
    nextTick(() => {
      scrollToBottom()
    })
  }
})

const submit = async () => {
  shouldDisableScorePad.value = true
  if (gameState.getCorrectingScore) {
    await gameState.correctScore().then(() => {
      scrollToBottom()
    }).catch((e) => {
      if (e) {
        useSonner.error(e.message, { dismissible: true })
      }
    }).finally(() => {
      shouldDisableScorePad.value = false
    })
  }
  else {
    await gameState.submitScore().then(() => {
      scrollToBottom()
    }).catch((e) => {
      if (e) {
        useSonner.error(e.message, { dismissible: true })
      }
    }).finally(() => {
      shouldDisableScorePad.value = false
    })
  }
}

const throwListContainer = ref<HTMLDivElement | null>(null)

function scrollToBottom() {
  const container = throwListContainer.value!
  if (container) {
    container.scrollTop = container.scrollHeight
  }
}

onMounted(() => {
  setTimeout(() => {
    scrollToBottom()
  }, 1000)
})

const { maximize, toggleMaximize } = inject<{ maximize: Ref<boolean>, toggleMaximize: () => void }>('toggleMaximize')!
onUnmounted(() => {
  if (maximize.value) {
    toggleMaximize()
  }
})
</script>

<template>
  <div
    v-if="gameState.concluded"
    class="flex flex-col justify-center items-center p-4 overflow-auto"
  >
    <div class="!flex !flex-col gap-4 h-full w-full">
      <div v-if="canLeave">
        <UiButton
          variant="destructive"
          @click="nextGameAction ? nextGameAction() : gameState.reset()"
        >
          {{ $t('Next game') }}
        </UiButton>
      </div>
      <GamesGameStatsOverview
        :name="gameState.getPlayerNameFor(GamePlayerSimple.HOME) + ' - ' + gameState.getPlayerNameFor(GamePlayerSimple.VISITOR)"
        :event-name="eventName ?? 'Local game'"
        :starting-score="gameState.getStartingScore"
        :legs-to-play="gameState.getLegsToPlay"
        :best-of="gameState.getBestOf"
        :winner="gameState.getWinnerSimplePlayer ? gameState.getPlayerNameFor(gameState.getWinnerSimplePlayer) : null"
        :bull-winner="gameState.getBullWinner"
        :player1="gameState.getPlayerNameFor(GamePlayerSimple.HOME)"
        :player2="gameState.getPlayerNameFor(GamePlayerSimple.VISITOR)"
        :player1-legs="gameState.getLegsWonFor(GamePlayerSimple.HOME)"
        :player2-legs="gameState.getLegsWonFor(GamePlayerSimple.VISITOR)"
        :player1-average="gameState.getGameAverage(GamePlayerSimple.HOME)"
        :player2-average="gameState.getGameAverage(GamePlayerSimple.VISITOR)"
        :player1-first9-average="gameState.getFirst9Average(GamePlayerSimple.HOME)"
        :player2-first9-average="gameState.getFirst9Average(GamePlayerSimple.VISITOR)"
        :player1-one-eighties="gameState.get180sFor(GamePlayerSimple.HOME)"
        :player2-one-eighties="gameState.get180sFor(GamePlayerSimple.VISITOR)"
        :player1-one-fourty="gameState.get140sFor(GamePlayerSimple.HOME)"
        :player2-one-fourty="gameState.get140sFor(GamePlayerSimple.VISITOR)"
        :player1-high-checkouts="gameState.getHighCheckoutsFor(GamePlayerSimple.HOME).map(throwItem => throwItem.s).join(', ')"
        :player2-high-checkouts="gameState.getHighCheckoutsFor(GamePlayerSimple.VISITOR).map(throwItem => throwItem.s).join(', ')"
        :player1-leg-darts-used="gameState.getLegDartsUsedFor(GamePlayerSimple.HOME)"
        :player2-leg-darts-used="gameState.getLegDartsUsedFor(GamePlayerSimple.VISITOR)"
      />
      <GameLegsOverview
        :starting-score="gameState.getStartingScore"
        :darts-throws="gameState.state.throws"
        :participants="[
          { id: gameState.state.homeTeam.participant_id, name: gameState.getPlayerNameFor(GamePlayerSimple.HOME) },
          { id: gameState.state.visitorTeam.participant_id, name: gameState.getPlayerNameFor(GamePlayerSimple.VISITOR) },
        ]"
      />
    </div>
  </div>
  <div
    v-else-if="gameState.hasBullWinner || viewOnly"
    class="flex portrait:flex-col landscape:flex-row font-mono flex-grow !max-h-full overflow-hidden select-none"
  >
    <div
      class="relative flex flex-col overflow-hidden !flex-1 portrait:h-2/3 landscape:w-2/3 landscape:h-full"
      :class="{ 'landscape:order-1': scorePadSide === 'left' }"
    >
      <div
        class="bg-gray-200 dark:bg-gray-700 px-2 py-0.5 items-center truncate overflow-hidden h-10 justify-center gap-2 grid grid-cols-3"
      >
        <div class="text-left sm:text-xl">
          <template v-if="gameState.bullWinnerIsHome">
            Bull winner
          </template>
        </div>
        <div class="sm:text-2xl md:text-3xl font-bold text-center flex flex-row gap-2">
          <div>
            <template v-if="gameState.isLegsToPlay">
              Leg: {{ gameState.getCurrentLeg }}/{{ gameState.getLegsToPlay }}
            </template>
            <template v-else>
              BO<strong>{{ gameState.getBestOf }}</strong>
            </template>
          </div>
          <UiDropdownMenu>
            <UiDropdownMenuTrigger>
              <button class="text-base flex items-center">
                <Icon name="tabler-menu-2" />
                {{ $t('MENU') }}
              </button>
            </UiDropdownMenuTrigger>
            <UiDropdownMenuContent class="w-56">
              <UiDropdownMenuItem
                :title="maximize ? $t('Minimize') : $t('Maximize')"
                icon="tabler-minimize"
                variant="ghost"
                @click="toggleMaximize"
              />
              <slot name="dropdown_options" />
            </UiDropdownMenuContent>
          </UiDropdownMenu>
        </div>
        <div class="text-right sm:text-xl">
          <template v-if="!gameState.bullWinnerIsHome">
            Bull winner
          </template>
        </div>
      </div>
      <div class="grid grid-cols-2 text-center shadow-lg relative">
        <div class="absolute top-15 w-full flex flex-col items-center sm:gap-4">
          <div
            v-if="gameState.hasBullWinner && !gameState.homeIsOnThrow"
            class="mt-0 size-12 mx-auto bg-primary rounded-full text-primary-foreground"
          >
            <Icon
              name="tabler-arrow-right"
              size="50px"
            />
          </div>
          <div
            v-if="gameState.hasBullWinner && gameState.homeIsOnThrow"
            class="mt-0 size-12 mx-auto bg-primary rounded-full text-primary-foreground"
          >
            <Icon
              name="tabler-arrow-left"
              size="50px"
            />
          </div>
          <slot name="list-actions" />
        </div>
        <player-box
          :players="gameState.getHomePlayers"
          :player="gameState.getPlayerForHomeTeam"
          :remaining-score="gameState.getRemainingScoreForHomeTeam"
          :show-average="gameState.shouldShowAverages"
          :is-on-throw="gameState.hasBullWinner ? gameState.homeIsOnThrow : false"
          :player-on-throw="gameState.hasBullWinner ? gameState.getPlayerToThrow : null"
          :leg-average="gameState.shouldShowAverages ? gameState.getLegAverage(gameState.getCurrentLeg, GamePlayerSimple.HOME) : null"
          :legs-won="gameState.getLegsWonForHomeTeam"
          :is-right-player="false"
          :legs-to-win="gameState.getLegsToWin"
          :legs-to-play="gameState.getLegsToPlay"
        />
        <player-box
          :players="gameState.getVisitorPlayers"
          :player="gameState.getPlayerForVisitorTeam"
          :remaining-score="gameState.getRemainingScoreForVisitorTeam"
          :show-average="gameState.shouldShowAverages"
          :is-on-throw="gameState.hasBullWinner ? gameState.visitorIsOnThrow : false"
          :player-on-throw="gameState.hasBullWinner ? gameState.getPlayerToThrow : null"
          :leg-average="gameState.shouldShowAverages ? gameState.getLegAverage(gameState.getCurrentLeg, GamePlayerSimple.VISITOR) : null"
          :legs-won="gameState.getLegsWonForVisitorTeam"
          :is-right-player="true"
          :legs-to-win="gameState.getLegsToWin"
          :legs-to-play="gameState.getLegsToPlay"
        />
      </div>
      <div class="flex flex-col flex-1 h-full overflow-hidden">
        <div
          ref="throwListContainer"
          class="flex-1 overflow-auto grid grid-cols-9 text-center md:pt-4 relative scrollbar"
          :class="{ 'pointer-events-none': disabled }"
        >
          <div class="flex flex-col col-span-4">
            <throw-list
              :darts-throws="gameState.getCurrentLegThrowsForHomeTeam"
              :on-score-tap="gameState.startScoreCorrection"
              :starting-score="gameState.getStartingScore"
              :correcting-score="gameState.getCorrectingScore"
              :can-correct-score="gameState.settingCanCorrectScore"
              :view-only="viewOnly"
            />
          </div>
          <div class="flex flex-col w-full pointer-events-none">
            <div
              v-for="(number, index) in gameState.getMostThrowsInCurrentLeg"
              :key="`player-throw-${index}`"
              class="text-[7vw] sm:text-[5.5vw] portrait:sm:text-[8vw] text-center border-b text-gray-300 dark:text-gray-500"
            >
              {{ number * 3 }}
            </div>
            <div id="botoflist" />
          </div>
          <div class="flex flex-col col-span-4">
            <throw-list
              :darts-throws="gameState.getCurrentLegThrowsForVisitorTeam"
              :on-score-tap="gameState.startScoreCorrection"
              :starting-score="gameState.getStartingScore"
              :correcting-score="gameState.getCorrectingScore"
              :can-correct-score="gameState.settingCanCorrectScore"
              :view-only="viewOnly"
            />
          </div>
        </div>
      </div>
      <GameScreenShortCuts
        :disabled="disabled"
        :view-only="viewOnly"
        :remaining-score-for-current-player="gameState.getRemainingScoreForParticipantOnThrow"
        :set-score-and-submit="setScoreAndSubmit"
      />
      <div
        v-if="message"
        class="absolute bottom-0 md:inset-0 w-full h-auto flex flex-col text-center px-2 text-7xl"
      >
        <div class="mx-auto my-auto p-4 bg-white dark:!bg-gray-600/50 backdrop-blur rounded-lg shadow-lg flex flex-row gap-2 items-center">
          <Icon
            v-if="message.home"
            name="tabler-arrow-left"
            class="w-8 h-8"
          />
          {{ message.message }}
          <Icon
            v-if="!message.home"
            name="tabler-arrow-right"
            class="w-8 h-8"
          />
        </div>
      </div>
      <div
        v-if="gameState.scorePadValue !== null || gameState.getCorrectingScore"
        class="absolute bottom-0 md:inset-0 w-full h-auto flex flex-col text-center px-2"
      >
        <div
          class="z-50 relative mx-auto my-auto flex flex-col items-center bg-white dark:bg-gray-600 rounded-xl p-4 border shadow-lg dark:border-gray-500"
        >
          <div
            v-if="gameState.getCorrectingScore"
          >
            {{ $t('Enter the new score for the throw and submit to update, tap red to cancel') }}
          </div>
          <div
            v-else-if="gameState.getRemainingScoreForParticipantOnThrow < 171"
          >
            {{ $t('Tap score to submit as remaining') }}
          </div>
          <div class="flex flex-col items-center w-full">
            <button
              v-touch:tap.prevent="() => onScoreTap()"
              class="rounded-full shadow-lg bg-gray-500 px-8 py-4 text-xl md:text-7xl items-center text-white flex gap-2"
              :class="{ 'bg-red-700 scale-125': hasError, 'bg-green-500': gameState.scorePadIsGameShot }"
            >
              <template v-if="gameState.scorePadIsGameShot">
                <span class="text-lg">{{ $t('Game shot') }}</span>
              </template>
              <template v-if="gameState.scorePadValue !== null">
                {{ gameState.scorePadValue }}
              </template>
              <template v-else>
                <span class="text-lg">
                  {{ $t('Enter new score') }}
                </span>
              </template>
            </button>
            <button
              v-if="gameState.getCorrectingScore"
              v-touch:tap.prevent="() => cancelORRemoveFromScore()"
              class="rounded-full shadow-lg bg-red-500 px-8 py-4 text-lg items-center text-white flex gap-2"
            >
              {{ $t('Cancel') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <ScorePad
      :should-show="gameState.showScorePad && !viewOnly"
      :disabled="disabled"
      :add-to-score="addToScore"
      :cancel-or-remove-from-score="cancelORRemoveFromScore"
      :submit="submit"
    >
      <template
        v-if="$slots.overlay"
        #overlay
      >
        <slot name="overlay" />
      </template>
    </ScorePad>
  </div>
  <div
    v-else
    class="text-4xl text-center p-4 gap-4 flex flex-col"
  >
    <div class="flex flex-row items-start">
      <UiDropdownMenu v-if="!hideMenu">
        <UiDropdownMenuTrigger>
          <button class="text-base flex items-center">
            <Icon name="tabler-menu-2" />
            {{ $t('MENU') }}
          </button>
        </UiDropdownMenuTrigger>
        <UiDropdownMenuContent class="w-56">
          <UiDropdownMenuItem
            :title="maximize ? $t('Minimize') : $t('Maximize')"
            icon="tabler-minimize"
            variant="ghost"
            @click="toggleMaximize"
          />
          <slot name="dropdown_options" />
        </UiDropdownMenuContent>
      </UiDropdownMenu>
    </div>
    <div v-if="customHeader">
      {{ customHeader }}
    </div>
    <div>
      <template v-if="gameState.isLegsToPlay">
        Play <strong>{{ gameState.getLegsToPlay }} legs</strong>
      </template>
      <template v-else>
        Best of <strong>{{ gameState.getBestOf }}</strong>
      </template>
      |
      <strong>{{ gameState.getStartingScore }}</strong>
    </div>
    <div>{{ $t('Select bull winner') }}</div>
    <div
      v-if="gameState.hasWriter && !noWriter"
      class="flex flex-row gap-4 mx-auto"
    >
      {{ $t('Writer:') }} {{ gameState.getWriterName }}
    </div>
    <div class="grid md:grid-cols-2 gap-4 md:max-w-[90%] mx-auto">
      <UiButton
        v-touch:tap.prevent="() => gameState.setBullWinner(GamePlayerSimple.HOME)"
        size="huge"
      >
        <span class="text-4xl truncate text-ellipsis justify-start">
          {{ gameState.getPlayerNameFor(GamePlayerSimple.HOME) }}
        </span>
      </UiButton>
      <UiButton
        v-touch:tap.prevent="() => gameState.setBullWinner(GamePlayerSimple.VISITOR)"
        size="huge"
      >
        <span class="text-4xl truncate text-ellipsis justify-start">
          {{ gameState.getPlayerNameFor(GamePlayerSimple.VISITOR) }}
        </span>
      </UiButton>
    </div>
  </div>
</template>
